import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useIvrList } from '@/features/ivr/api';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/date';
import { useMemo } from 'react';
import {
  DaySchedule,
  parseContinuousSchedule,
  VoiceCampaignFormValues,
  WEEK_DAYS,
  WeekDay,
  WeekSchedule,
} from '../../types';

/** Defaults applied when a day is enabled. */
const DEFAULT_DAY_START = '09:00:00';
const DEFAULT_DAY_END = '18:00:00';
export function StepSchedule({
  values,
  setValues,
}: Readonly<{
  values: VoiceCampaignFormValues;
  setValues: (patch: Partial<VoiceCampaignFormValues>) => void;
}>) {
  const ivrs = useIvrList();
  const schedule = useMemo(
    () => parseContinuousSchedule(values.continuousSchedule),
    [values.continuousSchedule],
  );

  /**
   * Serialize ONLY the enabled days — disabled days are dropped from the
   * object entirely, so the stored JSON stays light:
   *   {"monday":{"isEnable":true,"startTime":"09:00","endTime":"18:00"}}
   */
  const writeSchedule = (next: WeekSchedule) => {
    const sparse = Object.fromEntries(
      WEEK_DAYS.filter((d) => next[d].isEnable).map((d) => [d, next[d]]),
    ) as WeekSchedule;
    setValues({ continuousSchedule: JSON.stringify(sparse) });
  };

  const updateDay = (day: WeekDay, patch: Partial<DaySchedule>) => {
    writeSchedule({ ...schedule, [day]: { ...schedule[day], ...patch } });
  };

  const toggleDay = (day: WeekDay, enable: boolean) => {
    if (!enable) {
      writeSchedule({ ...schedule, [day]: { ...schedule[day], isEnable: false } });
      return;
    }
    updateDay(day, {
      isEnable: true,
      startTime: schedule[day].startTime || DEFAULT_DAY_START,
      endTime: schedule[day].endTime || DEFAULT_DAY_END,
    });
  };

  const applyPreset = (days: WeekDay[]) => {
    const next = { ...schedule };
    for (const d of WEEK_DAYS) {
      next[d] = days.includes(d)
        ? {
            isEnable: true,
            startTime: next[d].startTime || DEFAULT_DAY_START,
            endTime: next[d].endTime || DEFAULT_DAY_END,
          }
        : { ...next[d], isEnable: false };
    }
    writeSchedule(next);
  };

  const enabledCount = WEEK_DAYS.filter((d) => schedule[d].isEnable).length;
  const weekdays = WEEK_DAYS.filter((d) => !['saturday', 'sunday'].includes(d));

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle>Schedule</CardTitle>
              <CardDescription>
                Active date range, plus the weekly windows the campaign dials in.
              </CardDescription>
            </div>
            {enabledCount > 0 && (
              <Badge variant="outline" className="gap-1.5 font-normal">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {enabledCount} day{enabledCount > 1 ? 's' : ''} active
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>
                Start date
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <DatePicker
                value={values.startDate ? new Date(values.startDate) : undefined}
                onChange={(d) => setValues({ startDate: d ? formatDate(d) : '' })}
                placeholder="Pick a date"
                fromDate={new Date()}
              />
            </div>
            <div className="space-y-2">
              <Label>
                End date
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <DatePicker
                value={values.endDate ? new Date(values.endDate) : undefined}
                onChange={(d) => setValues({ endDate: d ? formatDate(d) : '' })}
                placeholder="Pick a date"
                fromDate={new Date()}
              />
            </div>
          </div>

          {/* ── Weekly windows ── */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label>
                Weekly schedule
                <span className="ml-1 text-destructive">*</span>
              </Label>
              {/* Presets */}
              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => applyPreset(weekdays)}
                >
                  Weekdays
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => applyPreset([...WEEK_DAYS])}
                >
                  All days
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => applyPreset([])}
                >
                  Clear
                </Button>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-border">
              {/* header */}
              <div className="grid grid-cols-[auto_8rem_1fr] items-center gap-3 border-b border-border bg-muted/50 px-4 py-2">
                <span className="w-9 text-2xs font-medium uppercase tracking-wider text-muted-foreground">
                  On
                </span>
                <span className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">
                  Day
                </span>
                <span className="text-right text-2xs font-medium uppercase tracking-wider text-muted-foreground">
                  Dialing window
                </span>
              </div>

              {WEEK_DAYS.map((day, i) => {
                const d = schedule[day];
                return (
                  <div
                    key={day}
                    className={cn(
                      'grid grid-cols-[auto_8rem_1fr] items-center gap-3 px-4 py-2 transition-colors',
                      i !== WEEK_DAYS.length - 1 && 'border-b border-border',
                      d.isEnable ? 'bg-primary/[0.04]' : 'bg-transparent hover:bg-muted/30',
                    )}
                  >
                    <Switch
                      checked={d.isEnable}
                      onCheckedChange={(v) => toggleDay(day, v)}
                      aria-label={`${d.isEnable ? 'Disable' : 'Enable'} ${day}`}
                    />
                    <span className="flex items-center gap-2 text-sm font-medium capitalize text-foreground">
                      <span
                        className={cn(
                          'h-1.5 w-1.5 shrink-0 rounded-full',
                          d.isEnable ? 'bg-emerald-500' : 'bg-muted-foreground/30',
                        )}
                      />
                      {day}
                    </span>
                    {d.isEnable ? (
                      <div className="flex items-center justify-end gap-3">
                        <Input
                          type="time"
                          className="w-36 justify-center text-sm font-semibold"
                          aria-label={`${day} start time`}
                          min="09:00"
                          max="21:00"
                          value={d.startTime}
                          onChange={(e) =>
                            updateDay(day, {
                              startTime: e.target.value ? `${e.target.value}:00` : '',
                            })
                          }
                          onKeyDown={(e) => e.preventDefault()}
                        />
                        <span className="text-sm text-muted-foreground">–</span>
                        <Input
                          type="time"
                          className="w-36 justify-center text-sm font-semibold"
                          aria-label={`${day} end time`}
                          min="09:00"
                          max="21:00"
                          value={d.endTime}
                          onChange={(e) =>
                            updateDay(day, {
                              endTime: e.target.value ? `${e.target.value}:00` : '',
                            })
                          }
                          onKeyDown={(e) => e.preventDefault()}
                        />
                      </div>
                    ) : (
                      <span className="text-right text-sm font-semibold text-muted-foreground">
                        Closed
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="space-y-1">
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Allowed dialing window:{' '}
                <span className="font-medium text-foreground">09:00 AM – 09:00 PM</span>
              </p>
            </div>
          </div>

          {/* ── continueWithBase — backend flag ── */}
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <div className="text-sm font-medium">Continue with existing base</div>
              <div className="text-xs text-muted-foreground">
                Keep the campaign running on its currently attached base.
              </div>
            </div>
            <Switch
              checked={values.continueWithBase}
              onCheckedChange={(v) => setValues({ continueWithBase: v })}
              aria-label="Continue with existing base"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── IVR hand-off ── */}
      <div className="space-y-2 rounded-md border border-border p-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Link a Custom IVR</div>
            <div className="text-xs text-muted-foreground">
              Hand the call off to one of your Custom IVR flows.
            </div>
          </div>
          <Switch
            checked={values.useIvr}
            onCheckedChange={(v) => setValues({ useIvr: v, ivrId: v ? values.ivrId : '' })}
          />
        </div>
        {values.useIvr && (
          <Select value={values.ivrId} onValueChange={(v) => setValues({ ivrId: v })}>
            <SelectTrigger>
              <SelectValue
                placeholder={
                  ivrs.isLoading
                    ? 'Loading…'
                    : (ivrs.data ?? []).length === 0
                      ? 'No IVRs configured'
                      : 'Pick an IVR'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {(ivrs.data ?? [])
                .filter((f) => String(f.status ?? '1') === '1')
                .map((f) => (
                  <SelectItem key={f.id} value={String(f.id)}>
                    {f.flowName}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </>
  );
}
